import { msalInstance, API_SCOPE } from "../core/services/msalInstance";
import { emitSessionExpired } from "./sessionEvents";

// The legacy (non-MSAL) login path already runs its own unconditional proactive
// refresh timer (LoginService.scheduleTokenRefresh, every 2h59m) independent of
// activity. Proactive refresh here only applies to the MSAL/SSO path, where no
// such background timer exists. Idle timeout applies to both paths equally.
export const PROACTIVE_REFRESH_INTERVAL_MS = 25 * 60 * 1000;
export const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
// Internal tick — deliberately shorter than the thresholds above so idle timeout
// fires close to the 30-minute mark rather than lagging behind a 25-minute tick.
const CHECK_INTERVAL_MS = 60 * 1000;
const ACTIVITY_EVENTS = ["mousemove", "keydown", "click", "touchstart", "scroll"] as const;

let lastActivityAt = Date.now();
let lastRefreshAttemptAt = Date.now();
let expiredEmitted = false;
let intervalId: ReturnType<typeof setInterval> | null = null;

const handleActivity = () => {
  lastActivityAt = Date.now();
};

const checkSession = () => {
  const now = Date.now();
  const idleFor = now - lastActivityAt;

  if (idleFor >= IDLE_TIMEOUT_MS) {
    if (!expiredEmitted) {
      expiredEmitted = true;
      emitSessionExpired();
    }
    return;
  }

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    // Legacy path — already covered by LoginService's own refresh timer.
    return;
  }

  if (now - lastRefreshAttemptAt < PROACTIVE_REFRESH_INTERVAL_MS) {
    return;
  }

  lastRefreshAttemptAt = now;
  msalInstance
    .acquireTokenSilent({ scopes: [API_SCOPE], account: accounts[0] })
    .catch((error) => {
      console.error("[activityTracker] Proactive token refresh failed:", error);
    });
};

export const startActivityTracking = (): void => {
  if (intervalId !== null) return;

  lastActivityAt = Date.now();
  lastRefreshAttemptAt = Date.now();
  expiredEmitted = false;

  ACTIVITY_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, handleActivity, { passive: true });
  });

  intervalId = setInterval(checkSession, CHECK_INTERVAL_MS);
};

export const stopActivityTracking = (): void => {
  ACTIVITY_EVENTS.forEach((eventName) => {
    window.removeEventListener(eventName, handleActivity);
  });

  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
};
